<script setup>
import DomainAnalysisForm from '@/Pages/DomainAnalysis/Partials/DomainAnalysisForm.vue';
import SiteLayout from '@/Layouts/Layout.vue';
import ExplainDmarc from '@/Pages/DomainAnalysis/Partials/ExplainDmarc.vue';
import ExplainSpf from '@/Pages/DomainAnalysis/Partials/ExplainSpf.vue';
import ExplainDkim from '@/Pages/DomainAnalysis/Partials/ExplainDkim.vue';
import { provide, watch } from 'vue';
import ExplainDomain from '@/Pages/DomainAnalysis/Partials/ExplainDomain.vue';
import ExplainTlsRpt from '@/Pages/DomainAnalysis/Partials/ExplainTlsRpt.vue';
import ExplainMtaSts from '@/Pages/DomainAnalysis/Partials/ExplainMtaSts.vue';
import ExplainBimi from '@/Pages/DomainAnalysis/Partials/ExplainBimi.vue';
import Card from '@/Components/Card/Card.vue';
import Alert from '@/Components/Alert.vue';
import {
  TOOL_DOMAIN_ANALYSIS,
  captureToolUsed,
  dmarcProviderHostsFromRua
} from '@/Common/Posthog.js';

const props = defineProps({
  domain: String,
  result: Object
});

provide('domain', props.domain);
provide('result', props.result);

const hasResults = !props.result.hasOwnProperty('detail');

watch(
  () => (hasResults ? props.domain : null),
  (testedDomain) => {
    if (!testedDomain) {
      return;
    }

    captureToolUsed({
      tool: TOOL_DOMAIN_ANALYSIS,
      properties: {
        tested_domain: testedDomain,
        overall_score: props.result.score_pct,
        impersonation_score: props.result.impersonation?.score,
        privacy_score: props.result.privacy?.score,
        branding_score: props.result.marketing?.score,
        dmarc_policy: props.result.records?.dmarc?.policy ?? '',
        dmarc_provider_hosts: dmarcProviderHostsFromRua(
          props.result.records?.dmarc?.aggregate_reporting_addresses
        )
      }
    });
  },
  { immediate: true }
);
</script>

<template>
  <SiteLayout
    :title="$t('domain_analysis.show.domain_analysis', { domain: domain })"
    :description="
      $t('domain_analysis.show.domain_analysis_description', { domain: domain })
    "
  >
    <DomainAnalysisForm :domain="domain" />
    <Card v-if="!hasResults">
      <Alert
        :title="$t('domain_analysis.show.record_not_found')"
        variant="danger"
      >
        {{ result.detail }}
      </Alert>
    </Card>

    <div v-if="hasResults" class="flex flex-col gap-2">
      <div class="text-lg font-bold md:text-xl">
        {{ $t('domain_analysis.show.impersonation') }}
      </div>
      <ExplainDmarc id="dmarc" />
      <ExplainSpf id="spf" />
      <ExplainDkim id="dkim" />
    </div>

    <div v-if="hasResults" class="flex flex-col gap-2">
      <div class="text-lg font-bold md:text-xl">
        {{ $t('domain_analysis.show.privacy') }}
      </div>
      <ExplainTlsRpt id="tls" />
      <ExplainMtaSts id="mta" />
    </div>

    <div v-if="hasResults" class="flex flex-col gap-2">
      <div class="text-lg font-bold md:text-xl">
        {{ $t('domain_analysis.show.branding') }}
      </div>
      <ExplainBimi id="bimi" />
    </div>
    <ExplainDomain v-if="hasResults" />
  </SiteLayout>
</template>
